import { PieChart, Pie, Cell, Label } from "recharts";
import { ChartContainer } from "../ui/chart";

const MacroCompoundRadialChart = ({ macroData, showMacroName = true, animate = true }) => {
  // Extract values from the data
  const { macro, amount, target } = macroData;
  const value = amount.value;
  const min = target?.min;
  const max = target?.max;

  // Define colors
  const mainGreen = "#598a56";
  const mainGreenDarker = "#3e6b3b"; // Darker shade for values above min
  const redColor = "#B86850"; // complementary red
  const backgroundGray = "hsl(var(--secondary))"; // Light gray for background
  const separator = "#ffffff";

  // Prepare chart data based on target conditions
  const prepareChartData = () => {
    // Initialize segments with a small gap
    const segments = [];

    // Case 1: No target - just show the value in mainGreen (full circle)
    if (!target || (target && !min && !max)) {
      segments.push({ name: macro.name, value: 100, fill: value !== 0 ? mainGreen : backgroundGray });
      return segments;
    }

    // Case 2: Only min target
    if (min && !max) {
      if (value < min) {
        // Value is less than min - show partial fill
        const percentage = (value / min) * 100;
        segments.push({ name: macro.name, value: percentage, fill: mainGreen });
        segments.push({ name: "remaining", value: 100 - percentage, fill: backgroundGray });
      } else {
        // Value meets or exceeds min - show min in green and excess in dark green
        const minPercentage = (min / value) * 100;
        const excessPercentage = 100 - minPercentage;
        segments.push({ name: `${macro.name}-min`, value: minPercentage, fill: mainGreen });
        segments.push({ name: `${macro.name}-above-min`, value: excessPercentage, fill: mainGreenDarker });
      }
      return segments;
    }

    // Case 3: Only max target
    if (!min && max) {
      if (value <= max) {
        // Value is within max - show partial fill proportional to how close to max
        const percentage = (value / max) * 100;
        segments.push({ name: macro.name, value: percentage, fill: mainGreen });
        segments.push({ name: "remaining", value: 100 - percentage, fill: backgroundGray });
      } else {
        // Value exceeds max - proportional segments
        const maxPercentage = (max / value) * 100;
        const excessPercentage = 100 - maxPercentage;
        segments.push({ name: `${macro.name}-max`, value: maxPercentage, fill: mainGreen });
        segments.push({ name: `${macro.name}-above-max`, value: excessPercentage, fill: redColor });
      }
      return segments;
    }

    // Case 4: Both min and max targets
    if (min && max) {
      if (value < min) {
        // Value is less than min - show partial fill
        const percentage = (value / min) * 100;
        segments.push({ name: macro.name, value: percentage, fill: mainGreen });
        segments.push({ name: "remaining", value: 100 - percentage, fill: backgroundGray });
      } else if (value <= max) {
        // Value is between min and max - full circle with proportional segments
        const minPercentage = (min / value) * 100;
        const betweenPercentage = 100 - minPercentage;
        segments.push({ name: `${macro.name}-min`, value: minPercentage, fill: mainGreen });
        segments.push({ name: `${macro.name}-between`, value: betweenPercentage, fill: mainGreenDarker });
      } else {
        // Value exceeds max - full circle with three proportional segments
        const minPercentage = (min / value) * 100;
        const maxPercentage = ((max - min) / value) * 100;
        const excessPercentage = 100 - minPercentage - maxPercentage;
        segments.push({ name: `${macro.name}-min`, value: minPercentage, fill: mainGreen });
        segments.push({ name: `${macro.name}-between`, value: maxPercentage, fill: mainGreenDarker });
        segments.push({ name: `${macro.name}-above-max`, value: excessPercentage, fill: redColor });
      }
      return segments;
    }

    return segments;
  };

  const chartData = prepareChartData();

  // Create chart config
  const chartConfig = {
    [macro.name]: {
      label: macro.name,
      color: mainGreen,
    },
  };

  return (
    <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[250px]">
      <PieChart>
        <Pie
          data={chartData}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={80}
          outerRadius={110}
          startAngle={240}
          endAngle={-60}
          paddingAngle={0}
          animationBegin={200}
          animationDuration={1000}
          isAnimationActive={animate}
        >
          {chartData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.fill}
              stroke={entry.stroke && separator}
              strokeWidth={entry.stroke ? 5 : 0}
              legendType={entry.legendType}
            />
          ))}
          <Label
            content={({ viewBox }) => {
              if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                return (
                  <text
                    x={viewBox.cx}
                    y={viewBox.cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="font-sans"
                  >
                    <tspan
                      x={viewBox.cx}
                      y={viewBox.cy - (showMacroName ? 17 : 8)}
                      className="fill-foreground text-3xl font-semibold"
                    >
                      {value.toLocaleString()}
                    </tspan>
                    {amount?.unit?.name && (
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy || 0) + (showMacroName ? 7 : 20)}
                        className="fill-secondaryText italic text-lg"
                      >
                        {amount.unit.name}
                      </tspan>
                    )}

                    {showMacroName && (
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy || 0) + 30}
                        className="fill-primaryText font-normal text-base"
                      >
                        {macro.name}
                      </tspan>
                    )}
                  </text>
                );
              }
            }}
          />
        </Pie>
      </PieChart>
    </ChartContainer>
  );
};

export default MacroCompoundRadialChart;
